import { forwardRef, useId, useRef, useState } from 'react';
import { DateTime } from './DateTime';
import { EmptyState } from './EmptyState';
import { FeedbackAction, InlineAlert } from './InlineAlert';
import { KeyValueList } from './KeyValueList';
import { ProviderBadge } from './ProviderBadge';
import { Section } from './Section';
import { StatusChip } from './StatusChip';
import type { Action, Tone, WithClassName } from './types';

export type DiffItem = {
  /** Stable key only; never used as a visible name. */
  id: string;
  change: 'add' | 'remove' | 'unchanged';
  label: string;
  detail?: string;
  provider?: string;
  /** Per-item count supplied by the server; people may overlap across items. */
  affectedPeople?: number;
};

export type DiffGroup = {
  id: string;
  label: string;
  provider?: string;
  items: readonly DiffItem[];
};

export type DiffPeopleCounts = {
  gaining?: number;
  losing?: number;
  totalAffected?: number;
};

/** Generic display contract, not a role publish endpoint response schema. */
export type DiffPreview = {
  previewVersion: string | number;
  draftVersion: string | number;
  calculatedAt: string;
  /** Unique counts supplied by the caller, never derived from item counts. */
  affectedPeople?: DiffPeopleCounts;
};

export type DiffListProps = WithClassName & {
  label: string;
  state: 'loading' | 'ready' | 'error';
  error?: string;
  onRetry?: () => void | Promise<void>;
  showUnchanged?: boolean;
  onShowUnchangedChange?: (show: boolean) => void;
  preview?: DiffPreview;
  onRecalculate?: () => void | Promise<void>;
  /** Only actions dependent on this preview; the caller owns their effects. */
  actions?: readonly Action[];
} & (
  | { items: readonly DiffItem[]; groups?: never }
  | { groups: readonly DiffGroup[]; items?: never }
);

const changes: readonly DiffItem['change'][] = ['add', 'remove', 'unchanged'];
const labels = { add: 'Add', remove: 'Remove', unchanged: 'Unchanged' } as const;
const signs = { add: '+', remove: '−', unchanged: '=' } as const;
const tones: Record<DiffItem['change'], Tone> = { add: 'success', remove: 'danger', unchanged: 'neutral' };

function Counts({ items }: { items: readonly DiffItem[] }) {
  return <div className="ui-diff__counts" role="group" aria-label="Item counts">
    {changes.map((change) => <StatusChip key={change} tone={tones[change]}
      label={`${labels[change]} (${items.filter((item) => item.change === change).length})`} />)}
  </div>;
}

function Items({ items, change }: { items: readonly DiffItem[]; change: DiffItem['change'] }) {
  if (!items.length) return null;
  return <ul className="ui-diff__list" aria-label={`${labels[change]} (${items.length})`}>
    {items.map((item) => {
      const name = item.label.trim() || 'Name unavailable';
      const verb = change === 'add' ? 'gain' : change === 'remove' ? 'lose' : 'keep access to';
      return <li key={item.id} className="ui-diff__item">
        <div className="ui-diff__identity">
          <span className={`ui-diff__change ui-tone-${tones[change]}`}>
            <span aria-hidden="true">{signs[change]}</span>{labels[change]}
          </span>
          <strong>{name}</strong>
          {item.provider && <ProviderBadge provider={item.provider} size="sm" />}
        </div>
        {item.detail && <p>{item.detail}</p>}
        {item.affectedPeople !== undefined && <p>{item.affectedPeople} people {verb} {name}</p>}
      </li>;
    })}
  </ul>;
}

export const DiffList = forwardRef<HTMLElement, DiffListProps>(function DiffList({
  label, items, groups, state, error, onRetry, showUnchanged, onShowUnchangedChange,
  preview, onRecalculate, actions, className,
}, ref) {
  const id = useId();
  const [localShowUnchanged, setShowUnchanged] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const recalculationPending = useRef(false);
  const allItems = groups ? groups.flatMap((group) => group.items) : items ?? [];
  const displayedGroups: readonly DiffGroup[] = groups ?? [{ id: 'items', label: '', items: allItems }];
  const unchanged = allItems.filter((item) => item.change === 'unchanged').length;
  const expanded = showUnchanged ?? localShowUnchanged;
  const stale = !!preview && preview.previewVersion !== preview.draftVersion;
  const busy = state === 'loading' || recalculating;
  const blockedReason = busy ? 'The preview is being recalculated. Wait before continuing.'
    : state === 'error' ? 'The preview is unavailable. Retry before continuing.'
      : stale ? 'This preview is stale. Recalculate for the current draft before continuing.' : undefined;
  const unchangedId = (group: DiffGroup) => `${id}-unchanged-${encodeURIComponent(group.id)}`;
  const people = preview?.affectedPeople;
  const peopleItems = [
    ...(people?.gaining !== undefined ? [{ key: 'gaining', label: 'Gaining access', value: `${people.gaining} people` }] : []),
    ...(people?.losing !== undefined ? [{ key: 'losing', label: 'Losing access', value: `${people.losing} people` }] : []),
    ...(people?.totalAffected !== undefined ? [{ key: 'total', label: 'Total affected', value: `${people.totalAffected} people` }] : []),
  ];

  async function recalculate() {
    if (recalculationPending.current || state === 'loading' || !onRecalculate) return;
    recalculationPending.current = true;
    setRecalculating(true);
    try { await onRecalculate(); }
    finally { recalculationPending.current = false; setRecalculating(false); }
  }

  return <Section ref={ref} title={label} className={`ui-diff ${className ?? ''}`}>
    {preview && <p className="ui-diff__metadata">
      Preview for draft version {preview.previewVersion} · Current draft version {preview.draftVersion}
      {' · '}Calculated <DateTime value={preview.calculatedAt} />
    </p>}
    {stale && <InlineAlert tone="warning" title="Preview is stale">
      The draft has changed. These results describe a different draft. Recalculate before continuing.
    </InlineAlert>}
    {onRecalculate && <FeedbackAction action={{ id: 'recalculate', label: 'Recalculate', run: recalculate,
      disabledReason: busy ? 'The preview is being recalculated.' : undefined }} />}
    {recalculating && <p role="status">Recalculating preview…</p>}
    {state === 'error' && <InlineAlert tone="danger" title="Preview unavailable"
      action={onRetry ? { id: 'retry', label: 'Retry', run: onRetry } : undefined}>
      {error || 'Unable to calculate the preview. Please try again.'}
    </InlineAlert>}
    {state === 'loading' && <p role="status">Loading impact…</p>}
    <div className="ui-diff__content" aria-busy={busy || undefined}>
      {state === 'loading' && <>
        <div className="ui-diff__skeleton" aria-hidden="true">&nbsp;</div>
        <div className="ui-diff__skeleton" aria-hidden="true">&nbsp;</div>
        <div className="ui-diff__skeleton" aria-hidden="true">&nbsp;</div>
      </>}
      {state === 'ready' && <>
        <Counts items={allItems} />
        {peopleItems.length > 0 && <div role="group" aria-label="Unique affected people">
          <KeyValueList items={peopleItems} />
        </div>}
        {!allItems.some((item) => item.change !== 'unchanged') && <EmptyState kind="no-changes"
          title="No access changes" description="This preview contains no additions or removals." />}
        {unchanged > 0 && <button type="button" className="ui-button ui-button--ghost ui-diff__toggle"
          aria-expanded={expanded}
          aria-controls={displayedGroups.filter((group) => group.items.some((item) => item.change === 'unchanged')).map(unchangedId).join(' ')}
          onClick={() => {
            if (showUnchanged === undefined) setShowUnchanged(!expanded);
            onShowUnchangedChange?.(!expanded);
          }}>{expanded ? 'Hide' : 'Show'} unchanged ({unchanged})</button>}
        {displayedGroups.map((group) => {
          const content = <>
            {groups && <Counts items={group.items} />}
            <Items items={group.items.filter((item) => item.change === 'add')} change="add" />
            <Items items={group.items.filter((item) => item.change === 'remove')} change="remove" />
            {group.items.some((item) => item.change === 'unchanged') && <div id={unchangedId(group)} hidden={!expanded}>
              <Items items={group.items.filter((item) => item.change === 'unchanged')} change="unchanged" />
            </div>}
          </>;
          return groups ? <Section key={group.id} title={group.label.trim() || 'Target name unavailable'} headingLevel={3}>
            {group.provider && <ProviderBadge provider={group.provider} size="sm" />}
            {content}
          </Section> : <div key={group.id}>{content}</div>;
        })}
        {allItems.some((item) => item.affectedPeople !== undefined) && <p className="ui-diff__note">
          People can appear in multiple rows. Per-item counts are not a unique-person total.
        </p>}
      </>}
    </div>
    {!!actions?.length && <div className="ui-structure-actions" role="group" aria-label="Preview actions">
      {actions.map((action) => <FeedbackAction key={action.id} action={{ ...action,
        disabledReason: blockedReason ?? action.disabledReason,
        run: () => { if (!blockedReason && !action.disabledReason && !recalculationPending.current) return action.run(); },
      }} />)}
    </div>}
  </Section>;
});
